import type { Asset } from '@/types/asset';
import { PERIOD_PATTERN_PRESETS } from '@/types/settings';
import type { CalculationInputs } from '@/types/depreciation';

export interface DepreciationCalculationResult {
  depreciationValue: number;
  closingNBV: number;
  method: string;
  formula: string;
  formulaBreakdown: string;
  depreciationPercent?: number | null;
  calculationInputs?: CalculationInputs;
}

// Default period ratios (4-4-5 pattern)
const DEFAULT_PERIOD_RATIOS = PERIOD_PATTERN_PRESETS['4-4-5'].ratios;

export class DepreciationCalculator {
  // Store the current period ratios configuration
  private static periodRatios: number[] = DEFAULT_PERIOD_RATIOS;

  /**
   * Set the period ratios configuration for Period Ratio depreciation
   * This should be called when asset options are loaded
   */
  static setPeriodRatios(ratios: number[]): void {
    if (ratios && ratios.length === 12 && ratios.every(r => r > 0)) {
      this.periodRatios = ratios;
    } else {
      this.periodRatios = DEFAULT_PERIOD_RATIOS;
    }
  }

  /**
   * Get the current period ratios configuration
   */
  static getPeriodRatios(): number[] {
    return [...this.periodRatios];
  }

  /**
   * Calculate depreciation for an asset based on its depreciation method
   */
  static calculateDepreciation(
    asset: Asset,
    periodDate: Date = new Date(),
    periodsPerYear: number = 12,
    periodRatiosOverride?: number[]
  ): DepreciationCalculationResult {
    const method = asset.depreciationMethod ?? 'Straight Line';

    // Handle "None" method
    if (method.toLowerCase() === 'none') {
      return {
        depreciationValue: 0,
        closingNBV: asset.netBookValue,
        method: 'None',
        formula: 'No depreciation applied',
        formulaBreakdown: 'Depreciation % = 0%\nDepreciation Value = £0.00',
        depreciationPercent: 0,
        calculationInputs: {
          method: 'None',
          residualValue: asset.residualValue ?? 0,
          formula: 'No depreciation applied',
          formulaBreakdown: 'Depreciation % = 0%\nDepreciation Value = £0.00',
        },
      };
    }

    const depreciableAmount = asset.netBookValue - (asset.residualValue ?? 0);
    const usefulLife = asset.usefulLife || 5; // Default to 5 years if not specified
    const totalPeriods = usefulLife * periodsPerYear;

    // Calculate periods elapsed since acquisition
    const periodsElapsed = this.calculatePeriodsElapsed(
      new Date(asset.acquisitionDate),
      periodDate,
      periodsPerYear
    );

    switch (method) {
      case 'Straight Line':
        return this.calculateStraightLine(asset, depreciableAmount, totalPeriods, periodsPerYear);

      case 'Reducing Balance':
      case 'Declining Balance':
        return this.calculateReducingBalance(
          asset,
          depreciableAmount,
          usefulLife,
          periodsPerYear,
          periodsElapsed
        );

      case 'Sum of Years Digits':
      case 'Sum of Digits':
        return this.calculateSumOfDigits(
          asset,
          depreciableAmount,
          usefulLife,
          periodsPerYear,
          periodsElapsed
        );

      case 'Period Ratio':
        return this.calculatePeriodRatio(asset, depreciableAmount, totalPeriods, periodsElapsed, periodsPerYear, periodRatiosOverride);

      default:
        // Default to Straight Line if method not recognized (falls through to Straight Line case)
        return this.calculateStraightLine(asset, depreciableAmount, totalPeriods, periodsPerYear);
    }
  }

  private static calculateStraightLine(
    asset: Asset,
    depreciableAmount: number,
    totalPeriods: number,
    periodsPerYear: number
  ): DepreciationCalculationResult {
    const usefulLife = asset.usefulLife || 5;
    const residualValue = asset.residualValue ?? 0;
    const postingsMade = asset.postingsMade ?? 0;
    const baseValueOn = asset.baseValueOn ?? 'remaining';
    const postingFrequency = asset.postingFrequency ?? 'Monthly';
    const totalAssetValue = asset.acquisitionCost;
    const endOfYearNBV = asset.netBookValue;
    
    let depreciationPerPeriod: number;
    let formula: string;
    let formulaBreakdown: string;
    
    // Per business rules: Base Value On determines the calculation method
    if (baseValueOn.toLowerCase() === 'remaining') {
      // Remaining Postings: (End of Year NBV - Residual Value) / (Total Postings - Postings Made)
      const remainingPostings = totalPeriods - postingsMade;
      depreciationPerPeriod = remainingPostings > 0 ? (endOfYearNBV - residualValue) / remainingPostings : 0;
      formula = '(End of Year NBV - Residual Value) / (Total Postings - Postings Made)';
      formulaBreakdown = `= (£${endOfYearNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })} - £${residualValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}) / (${totalPeriods} - ${postingsMade})\n= £${(endOfYearNBV - residualValue).toLocaleString('en-GB', { minimumFractionDigits: 2 })} / ${remainingPostings}\n= £${depreciationPerPeriod.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
    } else {
      // Original Postings: (Total Asset Value - Residual Value) / Total Postings
      depreciationPerPeriod = totalPeriods > 0 ? (totalAssetValue - residualValue) / totalPeriods : 0;
      formula = '(Total Asset Value - Residual Value) / Total Postings';
      formulaBreakdown = `= (£${totalAssetValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })} - £${residualValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}) / ${totalPeriods}\n= £${(totalAssetValue - residualValue).toLocaleString('en-GB', { minimumFractionDigits: 2 })} / ${totalPeriods}\n= £${depreciationPerPeriod.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
    }
    
    // Ensure we don't depreciate below residual value
    if (asset.netBookValue - depreciationPerPeriod < residualValue) {
      depreciationPerPeriod = Math.max(0, asset.netBookValue - residualValue);
    }
    
    const depreciationPercent = 100 / usefulLife;

    return {
      depreciationValue: depreciationPerPeriod,
      closingNBV: asset.netBookValue - depreciationPerPeriod,
      method: 'Straight Line',
      formula,
      formulaBreakdown,
      depreciationPercent,
      calculationInputs: {
        method: 'Straight Line',
        usefulLife,
        residualValue,
        postingFrequency,
        baseValueOn: baseValueOn.toLowerCase() === 'remaining' ? 'Remaining Postings' : 'Original Postings',
        totalPostings: totalPeriods,
        postingsMade,
        currentBaseValue: depreciationPerPeriod,
        formula,
        formulaBreakdown: `Depreciation % = 100 / Useful Life\n= 100 / ${usefulLife} years\n= ${depreciationPercent.toFixed(2)}%\n\n${formulaBreakdown}`,
      },
    };
  }

  private static calculateReducingBalance(
    asset: Asset,
    depreciableAmount: number,
    usefulLife: number,
    periodsPerYear: number,
    periodsElapsed: number
  ): DepreciationCalculationResult {
    // Per business rules: The percentage is user-defined or inherited from Asset Category, NOT calculated
    // Formula: ((Start of Year NBV - Residual Value) x (Percentage / 100)) / Periods per Year
    
    const residualValue = asset.residualValue ?? 0;
    const postingFrequency = asset.postingFrequency ?? 'Monthly';
    
    // Use user-defined percentage from asset, default to 20% if not set
    const annualPercentage = asset.depreciationPercentage ?? 20;
    
    // Start of Year NBV - for simplicity, we use current NBV as start of year
    const startOfYearNBV = asset.netBookValue;
    
    // Calculate depreciation: ((Start of Year NBV - Residual Value) x (Percentage / 100)) / Periods per Year
    const depreciableBase = startOfYearNBV - residualValue;
    const annualDepreciation = depreciableBase * (annualPercentage / 100);
    let depreciationValue = annualDepreciation / periodsPerYear;
    
    // Ensure we don't depreciate below residual value
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    const formula = '((Start of Year NBV - Residual Value) x (Percentage / 100)) / Periods per Year';
    const formulaBreakdown = `= ((£${startOfYearNBV.toLocaleString('en-GB', { minimumFractionDigits: 2 })} - £${residualValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}) x (${annualPercentage.toFixed(2)}% / 100)) / ${periodsPerYear}\n= (£${depreciableBase.toLocaleString('en-GB', { minimumFractionDigits: 2 })} x ${(annualPercentage / 100).toFixed(4)}) / ${periodsPerYear}\n= £${annualDepreciation.toLocaleString('en-GB', { minimumFractionDigits: 2 })} / ${periodsPerYear}\n= £${depreciationValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Reducing Balance',
      formula,
      formulaBreakdown,
      depreciationPercent: annualPercentage,
      calculationInputs: {
        method: 'Reducing Balance',
        depreciationPercentage: annualPercentage,
        residualValue,
        postingFrequency,
        startOfYearNBV,
        currentBaseValue: depreciationValue,
        formula,
        formulaBreakdown: `Depreciation % = ${annualPercentage.toFixed(2)}% (user-defined)\n\n${formulaBreakdown}`,
      },
    };
  }

  private static calculateSumOfDigits(
    asset: Asset,
    depreciableAmount: number,
    usefulLife: number,
    periodsPerYear: number,
    periodsElapsed: number
  ): DepreciationCalculationResult {
    // Per business rules document: Sum of Digits uses TOTAL POSTINGS, not years
    // Sum of Digits = Total Postings x (Total Postings + 1) / 2
    // Depreciation Value = (Total Asset Value - Residual Value) x ((Total Postings - Postings Made) / Sum of Digits)
    
    const residualValue = asset.residualValue ?? 0;
    const totalAssetValue = asset.acquisitionCost;
    const totalPostings = asset.totalPostings ?? (usefulLife * periodsPerYear);
    const postingsMade = asset.postingsMade ?? 0;
    
    // Calculate sum of digits based on total postings
    const sumOfDigits = (totalPostings * (totalPostings + 1)) / 2;
    
    // Calculate remaining postings
    let remainingPostings = totalPostings - postingsMade;
    remainingPostings = Math.max(1, remainingPostings); // At least 1
    
    // Depreciation value for this posting
    let depreciationValue = sumOfDigits > 0 
      ? (totalAssetValue - residualValue) * remainingPostings / sumOfDigits 
      : 0;
    
    // Ensure we don't depreciate below residual value
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    const formula = '(Total Asset Value - Residual Value) x ((Total Postings - Postings Made) / Sum of Digits)';
    const formulaBreakdown = `Sum of Digits = n x (n + 1) / 2\n= ${totalPostings} x (${totalPostings} + 1) / 2\n= ${sumOfDigits}\n\nDepreciation Value = (£${totalAssetValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })} - £${residualValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}) x (${remainingPostings} / ${sumOfDigits})\n= £${(totalAssetValue - residualValue).toLocaleString('en-GB', { minimumFractionDigits: 2 })} x ${(remainingPostings / sumOfDigits).toFixed(6)}\n= £${depreciationValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Sum of Digits',
      formula,
      formulaBreakdown,
      depreciationPercent: null, // N/A for Sum of Digits
      calculationInputs: {
        method: 'Sum of Digits',
        usefulLife,
        residualValue,
        totalPostings,
        postingsMade,
        sumOfDigits,
        remainingPostings,
        currentBaseValue: depreciationValue,
        formula,
        formulaBreakdown: `Depreciation % = N/A (not used for Sum of Digits)\n\n${formulaBreakdown}`,
      },
    };
  }

  private static calculatePeriodRatio(
    asset: Asset,
    depreciableAmount: number,
    totalPeriods: number,
    periodsElapsed: number,
    periodsPerYear: number = 12,
    periodRatiosOverride?: number[]
  ): DepreciationCalculationResult {
    // Per business rules document:
    // Depreciation % = 100 / Useful Life (Years) - same as Straight Line
    // Depreciation Value = ((Total Asset Value - Residual Value) / Total Postings) x (Period Weeks / Standard Period Weeks)
    
    const usefulLife = asset.usefulLife || 5;
    const residualValue = asset.residualValue ?? 0;
    const totalAssetValue = asset.acquisitionCost;
    const postingFrequency = asset.postingFrequency ?? 'Monthly';
    
    // Use override if provided, otherwise use the configured period ratios
    const ratios = periodRatiosOverride || this.periodRatios;
    
    // Calculate standard period weeks (average)
    const totalWeeks = ratios.reduce((sum, w) => sum + w, 0);
    const standardPeriodWeeks = totalWeeks / 12;
    
    // Get weeks in current period from configured ratios
    const currentPeriodInYear = (periodsElapsed % periodsPerYear) + 1;
    const weeksInCurrentPeriod = this.getWeeksInPeriod(currentPeriodInYear, ratios);
    
    // Calculate base depreciation per posting
    const baseDepreciationPerPosting = totalPeriods > 0 ? (totalAssetValue - residualValue) / totalPeriods : 0;
    
    // Adjust for period weeks
    let depreciationValue = baseDepreciationPerPosting * (weeksInCurrentPeriod / standardPeriodWeeks);

    // Ensure we don't depreciate below residual value
    if (asset.netBookValue - depreciationValue < residualValue) {
      depreciationValue = Math.max(0, asset.netBookValue - residualValue);
    }

    const depreciationPercent = 100 / usefulLife;

    const formula = '((Total Asset Value - Residual Value) / Total Postings) x (Period Weeks / Standard Period Weeks)';
    const formulaBreakdown = `Base per posting = (£${totalAssetValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })} - £${residualValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}) / ${totalPeriods}\n= £${baseDepreciationPerPosting.toLocaleString('en-GB', { minimumFractionDigits: 2 })}\n\nPeriod adjustment = ${weeksInCurrentPeriod} / ${standardPeriodWeeks.toFixed(2)}\n= ${(weeksInCurrentPeriod / standardPeriodWeeks).toFixed(4)}\n\nDepreciation Value = £${baseDepreciationPerPosting.toLocaleString('en-GB', { minimumFractionDigits: 2 })} x ${(weeksInCurrentPeriod / standardPeriodWeeks).toFixed(4)}\n= £${depreciationValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

    return {
      depreciationValue,
      closingNBV: asset.netBookValue - depreciationValue,
      method: 'Period Ratio',
      formula,
      formulaBreakdown,
      depreciationPercent,
      calculationInputs: {
        method: 'Period Ratio',
        usefulLife,
        residualValue,
        postingFrequency,
        totalPostings: totalPeriods,
        postingsMade: periodsElapsed,
        periodWeeks: weeksInCurrentPeriod,
        currentBaseValue: depreciationValue,
        formula,
        formulaBreakdown: `Depreciation % = 100 / Useful Life\n= 100 / ${usefulLife} years\n= ${depreciationPercent.toFixed(2)}%\n\n${formulaBreakdown}`,
      },
    };
  }

  /**
   * Get weeks in period from configured ratios
   */
  private static getWeeksInPeriod(periodNumber: number, ratios?: number[]): number {
    const periodRatios = ratios || this.periodRatios;
    // periodNumber is 1-based, array is 0-based
    const index = Math.max(0, Math.min(periodNumber - 1, periodRatios.length - 1));
    return periodRatios[index];
  }

  /**
   * Determine the pattern name based on ratios
   */
  private static getPatternName(ratios: number[]): string {
    const ratiosStr = ratios.join(',');
    if (ratiosStr === PERIOD_PATTERN_PRESETS['4-4-5'].ratios.join(',')) return '4-4-5';
    if (ratiosStr === PERIOD_PATTERN_PRESETS['4-5-4'].ratios.join(',')) return '4-5-4';
    if (ratiosStr === PERIOD_PATTERN_PRESETS['5-4-4'].ratios.join(',')) return '5-4-4';
    if (ratiosStr === PERIOD_PATTERN_PRESETS['equal'].ratios.join(',')) return 'Equal';
    return 'Custom';
  }

  private static calculatePeriodsElapsed(
    acquisitionDate: Date,
    periodDate: Date,
    periodsPerYear: number
  ): number {
    const monthsElapsed =
      (periodDate.getFullYear() - acquisitionDate.getFullYear()) * 12 +
      periodDate.getMonth() -
      acquisitionDate.getMonth();

    if (periodsPerYear === 12) {
      return Math.max(0, monthsElapsed);
    } else if (periodsPerYear === 4) {
      // Quarterly
      return Math.max(0, Math.floor(monthsElapsed / 3));
    } else if (periodsPerYear === 1) {
      // Annually
      return Math.max(0, Math.floor(monthsElapsed / 12));
    } else {
      // Default to monthly
      return Math.max(0, monthsElapsed);
    }
  }
}
